import "./style.css";
import { createAccountCommand, parseAccountResult, sendAccountCommand, type AccountAction } from "./account";
import {
  accountLinkTokensFromFragment,
  confirmEmailVerification,
  confirmPasswordReset,
  loadAccountFeatures,
  requestPasswordReset,
} from "./account-lifecycle";
import { loadRuntimeConfig } from "./config";
import { publishShellPause, restoreCanvasFocus } from "./shell-state";
import { startUnity } from "./unity";

const required = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Element fehlt: ${selector}`);
  return element;
};

const canvas = required<HTMLCanvasElement>("#unity-canvas");
const overlay = required<HTMLElement>("#loading-overlay");
const message = required<HTMLElement>("#loading-message");
const progress = required<HTMLElement>("#progress-bar");
const retry = required<HTMLButtonElement>("#retry-button");
const networkStatus = required<HTMLElement>("#network-status");
const dialog = required<HTMLDialogElement>("#info-dialog");
const dialogTitle = required<HTMLElement>("#dialog-title");
const dialogContent = required<HTMLElement>("#dialog-content");
const accountDialog = required<HTMLDialogElement>("#account-dialog");
const accountForm = required<HTMLFormElement>("#account-form");
const accountMode = required<HTMLSelectElement>("#account-mode");
const accountModeField = required<HTMLElement>("#account-mode-field");
const accountEmail = required<HTMLInputElement>("#account-email");
const accountEmailField = required<HTMLElement>("#account-email-field");
const accountPassword = required<HTMLInputElement>("#account-password");
const accountStatus = required<HTMLElement>("#account-status");
const accountSubmit = required<HTMLButtonElement>("#account-submit");
const accountLogout = required<HTMLButtonElement>("#account-logout");
const accountDelete = required<HTMLButtonElement>("#account-delete");
const accountReset = required<HTMLButtonElement>("#account-reset");
const accountVerify = required<HTMLButtonElement>("#account-verify");
const accountLinkTokens = accountLinkTokensFromFragment(window.location.hash);
let pendingResetToken: string | null = accountLinkTokens.reset;
const pendingVerificationToken = accountLinkTokens.verify;
let verificationConfirmed = false;
let profileRefreshRequested = false;
let verificationRefreshAttempts = 0;
if (pendingResetToken || pendingVerificationToken) {
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}

function setShellPause(reason: "modal" | "application_background" | "loading" | "error", active: boolean): void {
  publishShellPause(window.superWerwolfUnity, reason, active);
}

function closeOtherDialogs(target: HTMLDialogElement): void {
  for (const candidate of [dialog, accountDialog]) {
    if (candidate !== target && candidate.open) candidate.close();
  }
}

function openExclusiveDialog(target: HTMLDialogElement): void {
  closeOtherDialogs(target);
  setShellPause("modal", true);
  if (!target.open) target.showModal();
}

function onDialogClosed(): void {
  if (!dialog.open && !accountDialog.open) {
    setShellPause("modal", false);
    restoreCanvasFocus(canvas);
  }
}

function updateNetworkStatus(): void {
  const online = navigator.onLine;
  networkStatus.textContent = online ? "Online" : "Offline – Fortschritt pausiert";
  networkStatus.classList.toggle("status-offline", !online);
}

async function checkBackend(healthUrl: string): Promise<void> {
  try {
    const response = await fetch(healthUrl, { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) throw new Error(String(response.status));
    networkStatus.textContent = "Online · Server bereit";
    networkStatus.classList.remove("status-offline");
  } catch {
    networkStatus.textContent = navigator.onLine ? "Server nicht erreichbar" : "Offline – Fortschritt pausiert";
    networkStatus.classList.add("status-offline");
  }
}

async function boot(): Promise<void> {
  setShellPause("error", false);
  setShellPause("loading", true);
  retry.hidden = true;
  progress.style.width = "0%";
  message.textContent = "Konfiguration wird geladen …";
  overlay.hidden = false;

  try {
    const config = await loadRuntimeConfig();
    await checkBackend(config.healthUrl);
    try {
      const features = await loadAccountFeatures();
      accountReset.hidden = !features.password_reset_enabled;
    } catch {
      accountReset.hidden = true;
    }
    message.textContent = "Spielinhalte werden geladen …";
    await startUnity(canvas, config, (value) => {
      const percentage = Math.max(0, Math.min(100, Math.round(value * 100)));
      progress.style.width = `${percentage}%`;
      message.textContent = `Spielinhalte werden geladen … ${percentage}%`;
    });
    overlay.hidden = true;
    setShellPause("loading", false);
    canvas.focus();
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Unbekannter Fehler";
    message.textContent = `${reason}. Bitte Verbindung prüfen und erneut versuchen.`;
    retry.hidden = false;
    setShellPause("loading", false);
    setShellPause("error", true);
  }
}

const legalCopy: Record<string, { title: string; body: string }> = {
  privacy: {
    title: "Datenschutz",
    body: "Vor dem öffentlichen Release wird hier die geprüfte Datenschutzerklärung einschließlich Account-, Spielstands-, Kauf- und Werbedaten veröffentlicht.",
  },
  imprint: {
    title: "Impressum",
    body: "Das vollständige Anbieterkennzeichen wird vor der öffentlichen Bereitstellung aus der freigegebenen Rechtskonfiguration eingesetzt.",
  },
  terms: {
    title: "Nutzungsbedingungen",
    body: "Die Nutzungsbedingungen werden vor Store- und Web-Release gemeinsam mit Kauf-, Premium- und Accountregeln veröffentlicht.",
  },
};

function openInfo(key: string): void {
  const copy = legalCopy[key];
  if (!copy) return;
  dialogTitle.textContent = copy.title;
  dialogContent.textContent = copy.body;
  openExclusiveDialog(dialog);
}

function showInfo(title: string, body: string): void {
  dialogTitle.textContent = title;
  dialogContent.textContent = body;
  openExclusiveDialog(dialog);
}

function setPasswordResetMode(active: boolean): void {
  accountModeField.hidden = active;
  accountEmailField.hidden = active;
  accountEmail.required = !active;
  accountPassword.autocomplete = active ? "new-password" : "current-password";
  accountSubmit.textContent = active ? "Neues Passwort speichern" : "Weiter";
}

function openAccountDialog(registrationRequired = false): void {
  setPasswordResetMode(pendingResetToken !== null);
  accountMode.value = registrationRequired ? "register" : accountMode.value;
  accountStatus.textContent = pendingResetToken
    ? "Gib jetzt dein neues Passwort mit mindestens 10 Zeichen ein."
    : registrationRequired
      ? "Registriere dich jetzt, um Level 2 und Cloud-Save freizuschalten. Dein Gastfortschritt wird übernommen."
      : "Registriere dein Gastprofil oder melde dich mit einem bestehenden Account an.";
  accountStatus.classList.remove("form-error", "form-success");
  openExclusiveDialog(accountDialog);
  if (pendingResetToken) accountPassword.focus();
  else accountEmail.focus();
}

required<HTMLButtonElement>("#account-button").addEventListener("click", () => openAccountDialog());
required<HTMLButtonElement>("#account-close").addEventListener("click", () => accountDialog.close());
dialog.addEventListener("close", onDialogClosed);
accountDialog.addEventListener("close", onDialogClosed);
accountForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  try {
    if (pendingResetToken) {
      setAccountActionsBusy(true);
      accountStatus.textContent = "Passwort wird sicher aktualisiert …";
      await confirmPasswordReset(pendingResetToken, accountPassword.value);
      pendingResetToken = null;
      accountPassword.value = "";
      accountStatus.textContent = "Passwort geändert. Alle bisherigen Sitzungen wurden abgemeldet.";
      accountStatus.classList.add("form-success");
      setPasswordResetMode(false);
      setAccountActionsBusy(false);
      // Destroy any memory-only access token and restart against the remaining
      // guest cookie or a fresh guest session after the reset revocation.
      window.setTimeout(() => window.location.reload(), 1200);
      return;
    }
    const command = createAccountCommand(accountMode.value as AccountAction, accountEmail.value, accountPassword.value);
    setAccountActionsBusy(true);
    accountStatus.textContent = command.action === "register" ? "Account wird erstellt …" : "Anmeldung läuft …";
    accountStatus.classList.remove("form-error", "form-success");
    sendAccountCommand(window.superWerwolfUnity, command);
  } catch (error) {
    accountStatus.textContent = error instanceof Error ? error.message : "Accountaktion fehlgeschlagen.";
    accountStatus.classList.add("form-error");
    setAccountActionsBusy(false);
  }
});

function setAccountActionsBusy(busy: boolean): void {
  accountSubmit.disabled = busy;
  accountLogout.disabled = busy;
  accountDelete.disabled = busy;
  accountReset.disabled = busy;
  accountVerify.disabled = busy;
}

function sendLifecycleCommand(action: "logout" | "delete"): void {
  try {
    const command = createAccountCommand(action, "", accountPassword.value);
    setAccountActionsBusy(true);
    accountStatus.textContent = action === "delete"
      ? "Accountzugang und Spielstand werden gelöscht …"
      : "Abmeldung läuft …";
    accountStatus.classList.remove("form-error", "form-success");
    sendAccountCommand(window.superWerwolfUnity, command);
  } catch (error) {
    accountStatus.textContent = error instanceof Error ? error.message : "Accountaktion fehlgeschlagen.";
    accountStatus.classList.add("form-error");
    setAccountActionsBusy(false);
  }
}

accountLogout.addEventListener("click", () => sendLifecycleCommand("logout"));
accountDelete.addEventListener("click", () => {
  if (!window.confirm(
    "Accountzugang, Spielfortschritt und Coins dauerhaft löschen? Pseudonymisierte Zahlungs- und Transaktionsnachweise bleiben für Rückerstattungen und Missbrauchsschutz erhalten.",
  )) return;
  sendLifecycleCommand("delete");
});
accountReset.addEventListener("click", async () => {
  try {
    setAccountActionsBusy(true);
    accountStatus.textContent = "Reset-E-Mail wird angefordert …";
    const result = await requestPasswordReset(accountEmail.value);
    accountStatus.textContent = result.delivery_enabled
      ? "Falls der Account existiert, wurde eine Reset-E-Mail gesendet."
      : "Der E-Mail-Versand ist noch nicht eingerichtet.";
    accountStatus.classList.toggle("form-success", result.delivery_enabled);
  } catch (error) {
    accountStatus.textContent = error instanceof Error ? error.message : "Reset-Anfrage fehlgeschlagen.";
    accountStatus.classList.add("form-error");
  } finally {
    setAccountActionsBusy(false);
  }
});
accountVerify.addEventListener("click", () => {
  const command = createAccountCommand("send_verification", "", "");
  setAccountActionsBusy(true);
  sendAccountCommand(window.superWerwolfUnity, command);
});

window.superWerwolfAccountResult = (json) => {
  try {
    const result = parseAccountResult(json);
    profileRefreshRequested = false;
    accountStatus.textContent = result.message;
    accountStatus.classList.toggle("form-success", result.success);
    accountStatus.classList.toggle("form-error", !result.success);
    setAccountActionsBusy(false);
    accountLogout.hidden = result.isGuest;
    accountDelete.hidden = result.isGuest;
    accountVerify.hidden = result.isGuest || result.emailVerified || !result.emailDeliveryEnabled;
    if (verificationConfirmed && result.emailVerified) verificationConfirmed = false;
    else if (
      verificationConfirmed
      && !result.isGuest
      && !profileRefreshRequested
      && verificationRefreshAttempts < 3
    ) {
      verificationRefreshAttempts += 1;
      profileRefreshRequested = true;
      window.superWerwolfUnity?.SendMessage?.("SuperWerwolfSession", "RefreshAccountProfile");
    }
    accountReset.hidden = !result.isGuest || !result.emailDeliveryEnabled;
    if (result.registrationRequired) {
      if (!accountDialog.open) openAccountDialog(true);
    } else if (result.success) {
      accountPassword.value = "";
      window.setTimeout(() => accountDialog.close(), 700);
    }
  } catch {
    accountStatus.textContent = "Die Accountantwort konnte nicht verarbeitet werden.";
    accountStatus.classList.add("form-error");
    setAccountActionsBusy(false);
  }
};
window.superWerwolfOpenAccountGate = () => openAccountDialog(true);
document.querySelectorAll<HTMLButtonElement>("[data-legal]").forEach((button) => {
  button.addEventListener("click", () => openInfo(button.dataset.legal ?? ""));
});
retry.addEventListener("click", () => {
  setShellPause("error", false);
  void boot();
});
window.addEventListener("online", updateNetworkStatus);
window.addEventListener("offline", updateNetworkStatus);
window.addEventListener("blur", () => setShellPause("application_background", true));
window.addEventListener("focus", () => {
  if (!document.hidden) setShellPause("application_background", false);
});
document.addEventListener("visibilitychange", () => {
  setShellPause("application_background", document.hidden);
  if (!document.hidden && !dialog.open && !accountDialog.open) restoreCanvasFocus(canvas);
});
required<HTMLElement>("#copyright-year").textContent = String(new Date().getFullYear());

if (pendingResetToken) openAccountDialog();
if (pendingVerificationToken) {
  void confirmEmailVerification(pendingVerificationToken)
    .then(() => {
      verificationConfirmed = true;
      verificationRefreshAttempts = 0;
      accountVerify.hidden = true;
      if (window.superWerwolfUnity?.SendMessage) {
        profileRefreshRequested = true;
        window.superWerwolfUnity.SendMessage("SuperWerwolfSession", "RefreshAccountProfile");
      }
      showInfo("E-Mail bestätigt", "Deine E-Mail-Adresse wurde erfolgreich bestätigt.");
    })
    .catch((error: unknown) => showInfo(
      "Bestätigung fehlgeschlagen",
      error instanceof Error ? error.message : "Der Bestätigungslink ist ungültig oder abgelaufen.",
    ));
}

updateNetworkStatus();
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => void navigator.serviceWorker.register("/sw.js"));
}
void boot();
